import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ControlType,
  CustomValidatorService,
  FieldDef,
  MonthYearDateDef,
  OptionItem,
  SelectDef,
} from '@domgen/dgx-fe-dynamic-form-builder';
import {
  DeviceCategory,
  DeviceDetailsField,
  DeviceFormValue,
  PurchaseDate,
} from '@sky/util-models';
import { Observable } from 'rxjs';
import { purchasePriceOptions } from './data';

@Injectable()
export class DeviceFormConfigService {
  constructor(
    private readonly customValidatorService: CustomValidatorService
  ) {}

  deviceCategoryEnum = DeviceCategory;
  deviceDetailsField = DeviceDetailsField;
  maxAge: number | undefined;

  /**
   * Form Config Builder
   */
  getFormbuilderConfig(
    category: string,
    deviceTypesFormOptions$: Observable<OptionItem[]>,
    brandsFormOptions$: Observable<OptionItem[]>,
    loadingBrandSelection$?: Observable<boolean>,
    disableBrands$?: Observable<boolean>,
    initialForm?: DeviceFormValue | undefined
  ): FieldDef[] {
    // Limit upto 5 years for mobile & 8 years for non mobile journey
    this.maxAge = category === this.deviceCategoryEnum.MobilePhone ? 5 : 8;
    const yearOptions = (() => {
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - this.maxAge;
      const years = [];
      // Start with the current year and go back to the minimum year a cover can be provided for

      for (let i = currentYear; i >= minYear; i--) {
        years.push(i);
      }
      return years;
    })();

    const fieldDef: (FieldDef | undefined)[] = [
      this.deviceTypesInputDef(
        deviceTypesFormOptions$,
        category,
        initialForm?.deviceType
      ),
      this.brandsInputDef(
        brandsFormOptions$,
        category,
        loadingBrandSelection$,
        disableBrands$,
        initialForm?.brand
      ),
      this.purchaseDateInputDef(yearOptions, initialForm?.purchaseDate),
      this.purchasePriceInputDef(initialForm?.purchasePrice),
    ];

    if (category === this.deviceCategoryEnum.MobilePhone) {
      fieldDef.splice(0, 2);
      fieldDef.pop();
    }

    if (category === this.deviceCategoryEnum.Laptop) {
      fieldDef.shift();
    }

    return fieldDef.filter(Boolean) as FieldDef[];
  }

  private deviceTypesInputDef(
    deviceTypesFormOptions$: Observable<OptionItem[]>,
    category: string,
    initialValue?: string
  ): SelectDef | undefined {
    return {
      controlType: ControlType.SELECT,
      controlName: this.deviceDetailsField.Type,
      validators: [Validators.required],
      label: { text: `What kind of ${category} is it?` },
      placeholder: 'Select',
      optionsStream$: deviceTypesFormOptions$,
      initialValue,
    };
  }

  private brandsInputDef(
    brandsFormOptions$: Observable<OptionItem[]>,
    category: string,
    loadingBrandSelection$?: Observable<boolean>,
    disableBrands$?: Observable<boolean>,
    initialValue?: string
  ): SelectDef | undefined {
    return {
      controlType: ControlType.SELECT,
      controlName: this.deviceDetailsField.Brand,
      validators: [],
      label: { text: `Who made your ${category}?` },
      placeholder: 'Select',
      optionsStream$: brandsFormOptions$,
      loadingStream$: loadingBrandSelection$,
      disabledStream$: disableBrands$,
      showPreloader: true,
      initialValue,
    };
  }

  private purchaseDateInputDef(
    yearOptions: number[],
    initialValue?: PurchaseDate
  ): MonthYearDateDef | undefined {
    return {
      controlType: ControlType.MONTHYEARDATE,
      controlName: this.deviceDetailsField.PurchaseDate,
      validators: [
        Validators.required,
        this.customValidatorService.applianceAgeValidator(
          this.maxAge ? this.maxAge * 12 : null
        ),
        this.customValidatorService.partialDateValidator,
      ],
      validationMessages: [
        {
          type: 'required',
          message: 'Enter your purchase date',
        },
        { type: 'yearNotSelected', message: 'Select a year' },
        {
          type: 'applianceTooOld',
          message: `The age of your appliance should be less than ${
            this.maxAge ? this.maxAge : 8
          } years old`,
        },
        {
          type: 'applianceTooNew',
          message:
            'Enter the purchase date. This date must be today or in the past.',
        },
      ],
      label: { text: 'When did you buy it?' },
      monthOptions: new Array(12).fill(null).map((x, i) => i + 1),
      yearOptions,
      //TODO make hint, monthLabel, yearLabel optional in MonthYearDateDef interface
      hint: '',
      monthLabel: { text: '' },
      yearLabel: { text: '' },
      initialValue,
    };
  }

  private purchasePriceInputDef(initialValue?: string): SelectDef | undefined {
    return {
      controlType: ControlType.SELECT,
      controlName: this.deviceDetailsField.PurchasePrice,
      validators: [Validators.required],
      label: { text: 'How much did it cost?' },
      placeholder: 'Select',
      options: purchasePriceOptions,
      initialValue,
    };
  }
}
